import type React from 'react';
import { useCallback, useMemo } from 'react';

import { EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { Grid, Typography } from '@mui/material';
import { type Column } from '@tanstack/react-table';
import { type Atom, type SetStateAction, useAtom, useAtomValue, type WritableAtom } from 'jotai';
import { type RESET } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  drawDefinitionsAtomFamily,
  eventPairedDrawParticipantsAtomFamily,
  eventTypeAtomFamily,
  qualifyingDrawParticipantsAtomFamily,
  qualifyingEnabledAtomFamily,
} from 'atoms/events';
import { useStyles } from 'components/accordion/styles';
import TMXAccordion from 'components/accordion/TMXAccordion';
import StandardPaper from 'components/papers/standard/StandardPaper';
import AlternatesTable from 'components/tables/competitors/alternates/AlternatesTable';
import QualifyingParticipantsTable from 'components/tables/competitors/qualifying/QualifyingTable';
import SelectParticipantsTable from 'components/tables/eventsSplitDraws/splitDraws/SelectParticipantsTable';
import { useDoubleTeamsRemoveDialog } from 'containers/dialogs/DoubleTeamsRemoveDialog';
import useUISettings from 'hooks/useUISettings';
import { type PairParticipant } from 'typedefs/pair';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

import { getIsUpdateAlternatesColumns, getIsUpdateQualifyingColumns, getIsUpdateSelectedColumns } from './utils';

const { TEAM } = EventTypeEnum;

interface ActiveCompetitorsSectionProps {
  alternateParticipantsAtom: WritableAtom<
    ParticipantInterface[],
    [typeof RESET | SetStateAction<ParticipantInterface[]>],
    void
  >;
  selectedParticipantsAtom: Atom<ParticipantInterface[]>;
  isTeamPlayersTable?: boolean;
}

const ActiveCompetitorsSection: React.FC<React.PropsWithChildren<ActiveCompetitorsSectionProps>> = ({
  alternateParticipantsAtom,
  selectedParticipantsAtom,
  isTeamPlayersTable,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { tournamentId, eventId } = useParams<{ tournamentId: string; eventId: string }>();
  const eventType = useAtomValue(eventTypeAtomFamily(eventId));
  const drawDefinition = useAtomValue(drawDefinitionsAtomFamily(eventId))?.[0];
  const openDoubleTeamsRemoveDialog = useDoubleTeamsRemoveDialog();
  const [alternateParticipants, setAlternateParticipants] = useAtom(alternateParticipantsAtom);
  const [qualifyingParticipants, setQualifyingParticipants] = useAtom(qualifyingDrawParticipantsAtomFamily(eventId));
  const selectedParticipants = useAtomValue(selectedParticipantsAtom);
  const pairedDrawParticipants = useAtomValue(eventPairedDrawParticipantsAtomFamily(eventId));
  const isQualifyingEnabled = useAtomValue(qualifyingEnabledAtomFamily(eventId));
  const { tournamentDeskUI, updateSettings } = useUISettings() ?? {};

  const isTeam = useMemo(() => eventType === TEAM, [eventType]);
  const isAlternatesTeamTable = useMemo(() => isTeam && !isTeamPlayersTable, [isTeam, isTeamPlayersTable]);

  const showQualifyingTable = useMemo(
    () => isQualifyingEnabled && !isTeamPlayersTable,
    [isQualifyingEnabled, isTeamPlayersTable],
  );

  const savedSelectedTableColumns = useMemo(() => {
    if (isTeamPlayersTable) return tournamentDeskUI?.teamsAvailablePlayersColumns;
    else if (isTeam && !isTeamPlayersTable) return tournamentDeskUI?.teamsSelectedColumns;
    else return tournamentDeskUI?.singlesDoublesSelectedColumns;
  }, [isTeam, isTeamPlayersTable, tournamentDeskUI]);

  const savedAlternateTableColumns = useMemo(() => {
    if (isTeamPlayersTable) return tournamentDeskUI?.teamsAlternatePlayersColumns;
    else if (isTeam && !isTeamPlayersTable) return tournamentDeskUI?.teamsAlternateColumns;
    else return tournamentDeskUI?.singlesDoublesAlternateColumns;
  }, [isTeam, isTeamPlayersTable, tournamentDeskUI]);

  const savedQualifyingTableColumns = useMemo(
    () => (isTeam ? tournamentDeskUI?.teamsQualifyingColumns : tournamentDeskUI?.singlesDoublesQualifyingColumns),
    [isTeam, tournamentDeskUI],
  );

  const competitorsSectionTitles = useMemo(() => {
    if (isTeamPlayersTable)
      return { competitors: t('pyr'), selected: t('availablePlayers'), alternate: t('alternatePlayers') };
    if (isTeam) return { competitors: t('tmz'), selected: t('selectedTeams'), alternate: t('alternateTeams') };
    return { competitors: t('participants'), selected: t('selectedParticipants'), alternate: t('alternates') };
  }, [isTeam, isTeamPlayersTable, t]);

  const handleSetAlternatesEntryPositionsState = useCallback(
    (entryPositions: { participantId: string; entryPosition: number }[]) => {
      const updatedParticipants = entryPositions?.map((entry) =>
        alternateParticipants?.find((participant) => participant.participantId === entry.participantId),
      );
      setAlternateParticipants(updatedParticipants);
    },
    [alternateParticipants, setAlternateParticipants],
  );

  const handleSetQualifiersEntryPositionsState = useCallback(
    (entryPositions: { participantId: string; entryPosition: number }[]) => {
      const updatedParticipants = entryPositions?.map((entry) =>
        qualifyingParticipants?.find((participant) => participant.participantId === entry.participantId),
      );
      setQualifyingParticipants(updatedParticipants);
    },
    [qualifyingParticipants, setQualifyingParticipants],
  );

  const handleUnpairPlayers = useCallback(
    (participantId: string, participants: ParticipantInterface[]) => {
      const clickedParticipants = participants.find((participant) => {
        return participant.participantId === participantId;
      });
      const individualParticipants = clickedParticipants.individualParticipants || [];
      const firstParticipant = individualParticipants[0];
      const secondParticipant = individualParticipants[1];

      const item = (individual, pair): PairParticipant => {
        const pairedElsewhere = pairedDrawParticipants.some(
          (p) =>
            p.participantId !== pair.participantId && p.pair.some((i) => i?.participantId === individual.participantId),
        );
        return {
          participantId: pair.participantId,
          individualParticipantId: individual?.participantId,
          name: individual?.name || individual?.participantName,
          checked: false,
          pairedElsewhere,
        };
      };

      openDoubleTeamsRemoveDialog(eventId, [
        item(firstParticipant, clickedParticipants),
        item(secondParticipant, clickedParticipants),
      ]);
    },
    [eventId, openDoubleTeamsRemoveDialog, pairedDrawParticipants],
  );

  const handleSaveSelectedColumnsSelection = useCallback(
    (columns: Column<ParticipantInterface>[]) => {
      const selectedColumns = columns.map((column) => column.id);

      if (!getIsUpdateSelectedColumns({ isTeam, isTeamPlayersTable, selectedColumns, tournamentDeskUI })) return;

      const update = {
        teamsAvailablePlayersColumns: isTeamPlayersTable
          ? selectedColumns
          : tournamentDeskUI?.teamsAvailablePlayersColumns,
        teamsSelectedColumns: isTeam && !isTeamPlayersTable ? selectedColumns : tournamentDeskUI?.teamsSelectedColumns,
        singlesDoublesSelectedColumns: !isTeam ? selectedColumns : tournamentDeskUI?.singlesDoublesSelectedColumns,
      };
      updateSettings(update);
    },
    [tournamentDeskUI, isTeam, isTeamPlayersTable, updateSettings],
  );

  const handleSaveQualifyingColumnsSelection = useCallback(
    (columns: Column<ParticipantInterface>[]) => {
      const selectedColumns = columns.map((column) => column.id);

      if (!getIsUpdateQualifyingColumns({ isTeam, selectedColumns, tournamentDeskUI })) return;

      const update = {
        teamsQualifyingColumns: isTeam ? selectedColumns : tournamentDeskUI?.teamsQualifyingColumns,
        singlesDoublesQualifyingColumns: !isTeam ? selectedColumns : tournamentDeskUI?.singlesDoublesQualifyingColumns,
      };
      updateSettings(update);
    },
    [tournamentDeskUI, isTeam, updateSettings],
  );

  const handleSaveAlternatesColumnsSelection = useCallback(
    (columns: Column<ParticipantInterface>[]) => {
      const selectedColumns = columns.map((column) => column.id);

      if (!getIsUpdateAlternatesColumns({ isTeam, isTeamPlayersTable, selectedColumns, tournamentDeskUI })) return;

      const update = {
        teamsAlternatePlayersColumns: isTeamPlayersTable
          ? selectedColumns
          : tournamentDeskUI?.teamsAlternatePlayersColumns,
        teamsAlternateColumns:
          isTeam && !isTeamPlayersTable ? selectedColumns : tournamentDeskUI?.teamsAlternateColumns,
        singlesDoublesAlternateColumns: !isTeam ? selectedColumns : tournamentDeskUI?.singlesDoublesAlternateColumns,
      };
      updateSettings(update);
    },
    [tournamentDeskUI, isTeam, isTeamPlayersTable, updateSettings],
  );

  return useMemo(
    () => (
      <TMXAccordion
        unmountOnClose
        accordionSummaryChildren={
          <Grid container direction="column" spacing={1}>
            <Grid item>
              <Typography sx={{ marginBottom: 0 }} variant="h1">
                {competitorsSectionTitles.competitors}
              </Typography>
            </Grid>
            <Grid container item className={classes.accordionSummaryDetails} spacing={2}>
              <Grid item>
                <Typography variant="body2">{`${isTeamPlayersTable ? t('availablePlayers') : t('selected')} (${
                  selectedParticipants.length
                })`}</Typography>
              </Grid>
              {isQualifyingEnabled && !isTeamPlayersTable && (
                <Grid item>
                  <Typography variant="body2">{`${t('qualifying')} (${qualifyingParticipants.length})`}</Typography>
                </Grid>
              )}
              <Grid item>
                <Typography variant="body2">{`${isTeamPlayersTable ? t('alternatePlayers') : t('alternates')} (${
                  alternateParticipants.length
                })`}</Typography>
              </Grid>
            </Grid>
          </Grid>
        }
        defaultExpanded={true}
      >
        <StandardPaper className="competitors-table">
          <Grid container direction="row" justifyContent="space-between" spacing={4}>
            <Grid item xs={12}>
              <SelectParticipantsTable
                alternateDrawParticipants={alternateParticipants}
                columnsSelection={savedSelectedTableColumns}
                drawDefinition={drawDefinition}
                entries={selectedParticipants}
                isQualifyingEnabled={isQualifyingEnabled}
                qualifyingParticipants={qualifyingParticipants}
                saveColumnsSelection={handleSaveSelectedColumnsSelection}
                title={competitorsSectionTitles.selected}
                onUnpairPlayers={handleUnpairPlayers}
              />
            </Grid>
            {showQualifyingTable && (
              <Grid item xs={12}>
                <QualifyingParticipantsTable
                  columnsSelection={savedQualifyingTableColumns}
                  drawDefinition={drawDefinition}
                  qualifyingDrawParticipants={qualifyingParticipants}
                  saveColumnsSelection={handleSaveQualifyingColumnsSelection}
                  setEntryPositionsState={handleSetQualifiersEntryPositionsState}
                  onUnpairPlayers={handleUnpairPlayers}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <AlternatesTable
                alternateDrawParticipants={alternateParticipants}
                alternateParticipantsAtom={alternateParticipantsAtom}
                columnsSelection={savedAlternateTableColumns}
                isQualifyingEnabled={isQualifyingEnabled}
                isTeamPlayersTable={isTeamPlayersTable}
                isTeamTable={isAlternatesTeamTable}
                saveColumnsSelection={handleSaveAlternatesColumnsSelection}
                setEntryPositionsState={handleSetAlternatesEntryPositionsState}
                title={competitorsSectionTitles.alternate}
                tournamentId={tournamentId}
                onUnpairPlayers={handleUnpairPlayers}
              />
            </Grid>
          </Grid>
        </StandardPaper>
      </TMXAccordion>
    ),
    [
      competitorsSectionTitles.competitors,
      competitorsSectionTitles.selected,
      competitorsSectionTitles.alternate,
      classes.accordionSummaryDetails,
      isTeamPlayersTable,
      t,
      selectedParticipants,
      isQualifyingEnabled,
      qualifyingParticipants,
      alternateParticipants,
      savedSelectedTableColumns,
      drawDefinition,
      handleSaveSelectedColumnsSelection,
      handleUnpairPlayers,
      showQualifyingTable,
      savedQualifyingTableColumns,
      handleSaveQualifyingColumnsSelection,
      handleSetQualifiersEntryPositionsState,
      alternateParticipantsAtom,
      savedAlternateTableColumns,
      isAlternatesTeamTable,
      handleSaveAlternatesColumnsSelection,
      handleSetAlternatesEntryPositionsState,
      tournamentId,
    ],
  );
};

export default ActiveCompetitorsSection;
